// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Scale } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Badge, EmptyState, Select, Pagination, DataTable, Skeleton, Input } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useListSort } from '@/hooks/useListSort'
import { useDebtCases, type DebtCaseFilters } from '@/hooks/useDebtCases'
import { useProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore } from '@/stores'
import type { DebtCase, DebtCaseStage } from '@/types'

function getStageBadgeVariant(stage: DebtCaseStage): 'warning' | 'error' | 'success' | 'ghost' {
  switch (stage) {
    case 'notice_1':
    case 'notice_2':
      return 'warning'
    case 'final_notice':
    case 'legal':
      return 'error'
    case 'resolved':
      return 'success'
    default:
      return 'ghost'
  }
}

function getDaysOverdueClass(days: number): string {
  if (days >= 90) return 'text-error font-semibold'
  if (days >= 60) return 'text-warning font-semibold'
  if (days >= 30) return 'text-warning font-semibold'
  return ''
}

interface PipelineCardProps {
  stage: DebtCaseStage
  label: string
  sub: string
  count: number
  active: boolean
  onClick: () => void
}

function PipelineCard({ stage, label, sub, count, active, onClick }: PipelineCardProps) {
  const variant = getStageBadgeVariant(stage)
  const borderClass = active ? 'ring-2 ring-primary' : ''
  const bgClass = variant === 'error' ? 'bg-error/10' : variant === 'warning' ? 'bg-warning/10' : 'bg-base-200'

  return (
    <Card
      as="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shadow-sm border border-base-300 text-left w-full ${borderClass}`}
      bodyClassName={`p-4 rounded-xl ${bgClass}`}
    >
      <div className="text-2xl font-bold">{count}</div>
      <div className="font-medium text-sm">{label}</div>
      <div className="text-xs text-base-content/60">{sub}</div>
    </Card>
  )
}

export function DebtCaseListPage() {
  const { t } = useTranslation('debt_collection')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<DebtCaseFilters>({})
  const [customerSearch, setCustomerSearch] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  // EP-97: collections queue — unresolved default (server-side), most-overdue-first.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'due_at',
    defaultOrder: 'asc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data, isLoading, isFetching, error } = useDebtCases(page, {
    ...filters,
    property_id: selectedPropertyId ?? undefined,
    customer_search: customerSearch || undefined,
    ...sortParams,
  })
  const { data: propertiesData } = useProperties()

  const cases = data?.data ?? []
  const properties = propertiesData?.data ?? []

  // Compute pipeline stage counts from the list data
  // We fetch all cases with no stage filter for pipeline counts
  const { data: allData } = useDebtCases(1, { ...filters, property_id: selectedPropertyId ?? undefined, stage: undefined, customer_search: customerSearch || undefined })
  const allCases = allData?.data ?? []

  const stageCounts: Record<DebtCaseStage, number> = {
    notice_1: 0,
    notice_2: 0,
    final_notice: 0,
    legal: 0,
    resolved: 0,
  }
  allCases.forEach((c) => {
    stageCounts[c.stage] = (stageCounts[c.stage] ?? 0) + 1
  })

  const pipelineStages: Array<{ stage: DebtCaseStage; sub: string }> = [
    { stage: 'notice_1', sub: t('pipeline.notice_1_sub') },
    { stage: 'notice_2', sub: t('pipeline.notice_2_sub') },
    { stage: 'final_notice', sub: t('pipeline.final_notice_sub') },
    { stage: 'legal', sub: t('pipeline.legal_sub') },
  ]

  const stageOptions = [
    { value: '', label: tc('labels.all') },
    { value: 'notice_1', label: t('stages.notice_1') },
    { value: 'notice_2', label: t('stages.notice_2') },
    { value: 'final_notice', label: t('stages.final_notice') },
    { value: 'legal', label: t('stages.legal') },
    { value: 'resolved', label: t('stages.resolved') },
  ]

  const propertyOptions = [
    { value: '', label: tc('labels.all_properties') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const columns: Column<DebtCase>[] = [
    {
      key: 'invoice_number',
      header: t('fields.invoice'),
      render: (dc) => <span className="font-mono font-medium">{dc.invoice_number}</span>,
      hideBelow: 'md',
    },
    {
      key: 'customer_name',
      header: t('fields.customer'),
      render: (dc) => dc.customer_name,
    },
    {
      key: 'unit_name',
      header: t('fields.unit'),
      render: (dc) => dc.unit_name ?? '—',
      hideBelow: 'md',
    },
    {
      key: 'property_name',
      header: t('fields.property'),
      render: (dc) => dc.property_name ?? '—',
      hideBelow: 'lg',
    },
    {
      // EP-97: column key === backend sort key (invoices.total).
      key: 'amount',
      header: t('fields.amount_due'),
      sortable: true,
      render: (dc) => (
        <span className="font-medium">
          {formatBaht(Number(dc.amount_due), { decimals: 2 })}
        </span>
      ),
    },
    {
      // EP-97: days_overdue is derived; the server sorts on the joined
      // invoices.due_at (asc ≡ most-overdue-first), so the header sorts by due_at.
      key: 'due_at',
      header: t('fields.days_overdue'),
      sortable: true,
      render: (dc) => (
        <span className={getDaysOverdueClass(dc.days_overdue)}>{dc.days_overdue}</span>
      ),
      hideBelow: 'lg',
    },
    {
      key: 'stage',
      header: t('fields.stage'),
      sortable: true,
      render: (dc) => (
        <Badge variant={getStageBadgeVariant(dc.stage)}>{t(`stages.${dc.stage}`)}</Badge>
      ),
    },
  ]

  const activeDebtFilterCount = [filters.stage || '', selectedPropertyId ? '1' : ''].filter(Boolean).length

  // EP-97: mobile sort options mirror the allow-listed sort keys.
  const sortOptions = [
    { value: 'due_at', label: tc('sort.by_most_overdue') },
    { value: 'amount', label: tc('sort.by_amount') },
    { value: 'stage', label: tc('sort.by_status') },
    { value: 'created_at', label: tc('sort.by_newest') },
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('title') }]} />

      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        isRefreshing={isFetching && !isLoading}
        // Property scope top-right (TC-MUI-A5 titleAction, mobile-only; desktop
        // picker stays in the filter card) — consistent across list pages.
        titleAction={
          <Select
            data-testid="page-property-picker-mobile"
            aria-label={tc('labels.select_property')}
            size="sm"
            value={selectedPropertyId ? String(selectedPropertyId) : ''}
            onChange={(e) => {
              setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
              setPage(1)
            }}
            options={propertyOptions}
          />
        }
      />

      {/* Pipeline Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {pipelineStages.map(({ stage, sub }) => (
          <PipelineCard
            key={stage}
            stage={stage}
            label={t(`pipeline.${stage}`)}
            sub={sub}
            count={stageCounts[stage]}
            active={filters.stage === stage}
            onClick={() => {
              setFilters((f) => ({ ...f, stage: f.stage === stage ? undefined : stage }))
              setPage(1)
            }}
          />
        ))}
      </div>

      {/* Filters */}
      <Card>
        {/* Search — always visible */}
        <div className="w-full min-w-0">
          <Input
            label={tc('actions.search')}
            placeholder={`${tc('actions.search')} ${t('fields.customer').toLowerCase()}...`}
            value={customerSearch}
            onChange={(e) => {
              setCustomerSearch(e.target.value)
              setPage(1)
            }}
          />
        </div>

        {/* Mobile-only toggle */}
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
          aria-expanded={mobileFiltersOpen}
          aria-controls="debt-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeDebtFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeDebtFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="debt-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          {/* EP-97: mobile column sort (desktop uses sortable DataTable headers). */}
          <div className="w-full sm:hidden">
            <MobileSortControl
              options={sortOptions}
              value={sort}
              order={order}
              onChange={handleSort}
              onToggleOrder={handleToggleOrder}
            />
          </div>
          <Select
            label={t('fields.stage')}
            value={filters.stage ?? ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, stage: (e.target.value as DebtCaseStage) || undefined }))
              setPage(1)
            }}
            options={stageOptions}
            className="w-full sm:w-48 min-w-0"
          />
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState title={tc('errors.something_went_wrong')} />
        </Card>
      ) : !cases.length ? (
        <Card>
          <EmptyState
            icon={<Scale className="w-12 h-12" />}
            title={t('empty.title')}
            description={t('empty.description')}
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={cases}
            keyExtractor={(dc) => dc.id}
            onRowClick={(dc) => navigate(`/debt-collection/${dc.id}`)}
            sortBy={sort}
            sortOrder={order}
            onSort={handleSort}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
